import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { SearchDialog, Shell, type ReadingSize, type Theme } from "./components";
import { localizeHref, localizedRoute, navigate, ROUTE_FOCUS_EVENT, usePathname } from "./navigation";
import { AnalyticsConsent } from "./AnalyticsConsent";
import { getXQuoteIdFromHash, trackAtlasEventOnce, trackPageView } from "./analytics";
import { HomePage } from "./pages/HomePage";
import { LocaleProvider, preloadAtlasLocale, readAtlasLocale, type Language } from "./locale";
import { getHomeIllustration } from "./illustrations";
import { matchEssayAudioRoute } from "./essayAudio";
import seoLabels from "./seo-labels.json";

type SeoIntentCopy = {
  utilityDescriptions: Record<string, string>;
  partTitles: Record<string, string>;
  chapterTitles: Record<string, string>;
  essayTitles: Record<string, string>;
};
type SeoIntents = Record<Language, SeoIntentCopy>;

const THEME_KEY = "atlas2:theme:v1";
const SIZE_KEY = "atlas2:reading-size:v1";
const SCROLL_PREFIX = "atlas2:scroll:v1:";
const LANGUAGE_KEY = "atlas:language:v1";
const PUBLIC_ORIGIN = "https://atlas.synergylm.com";
const ROUTE_LOADING_COPY: Record<Language, string> = {
  en: "Loading the selected Atlas page",
  uk: "Завантаження обраної сторінки Атласу",
  ru: "Загрузка выбранной страницы Атласа",
};
const AuthorsPage = lazy(() => import("./pages/AuthorsPage").then((module) => ({ default: module.AuthorsPage })));
const ChapterPage = lazy(() => import("./pages/ChapterPage").then((module) => ({ default: module.ChapterPage })));
const EssayPage = lazy(() => import("./pages/EssayPage").then((module) => ({ default: module.EssayPage })));
const LibraryPage = lazy(() => import("./pages/LibraryPage").then((module) => ({ default: module.LibraryPage })));
const MethodPage = lazy(() => import("./pages/MethodPage").then((module) => ({ default: module.MethodPage })));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage })));
const PartPage = lazy(() => import("./pages/PartPage").then((module) => ({ default: module.PartPage })));
const SourcesPage = lazy(() => import("./pages/SourcesPage").then((module) => ({ default: module.SourcesPage })));
const TranscriptPage = lazy(() => import("./pages/TranscriptPage").then((module) => ({ default: module.TranscriptPage })));
const AUDIO_SEO_COPY: Record<Language, { suffix: string; description: (essayTitle: string) => string }> = {
  en: {
    suffix: "Audio and Transcript",
    description: (essayTitle) => `A complete English audio discussion of “${essayTitle},” with a reviewed transcript, Ukrainian and Russian translations, and clickable timestamps.`,
  },
  uk: {
    suffix: "Аудіо та розшифрування",
    description: (essayTitle) => `Повна англомовна аудіодискусія про есе «${essayTitle}» з перевіреним розшифруванням, перекладами та клікабельними таймкодами.`,
  },
  ru: {
    suffix: "Аудио и расшифровка",
    description: (essayTitle) => `Полная англоязычная аудиодискуссия по эссе «${essayTitle}» с проверенной расшифровкой, переводами и кликабельными таймкодами.`,
  },
};

function fixedTopUiInset() {
  let inset = 0;
  for (const element of document.querySelectorAll<HTMLElement>(".mobile-header, .page-beacon")) {
    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.position !== "fixed") continue;
    const { bottom } = element.getBoundingClientRect();
    if (Number.isFinite(bottom)) inset = Math.max(inset, bottom);
  }
  return Math.max(0, Math.ceil(inset));
}

function RouteReady({
  location,
  onReady,
  children,
}: {
  location: string;
  onReady: (location: string) => void;
  children: ReactNode;
}) {
  useEffect(() => {
    onReady(location);
  }, [location, onReady]);
  return children;
}

function initialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function initialSize(): ReadingSize {
  if (typeof window === "undefined") return "normal";
  return localStorage.getItem(SIZE_KEY) === "large" ? "large" : "normal";
}

export function App() {
  const location = usePathname();
  const requestedPathname = location.split(/[?#]/)[0] || "/";
  const { language, pathname } = localizedRoute(requestedPathname);
  const publicPathname = localizeHref(pathname, language);
  const atlas = readAtlasLocale(language);
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [readingSize, setReadingSize] = useState<ReadingSize>(initialSize);
  const [searchOpen, setSearchOpen] = useState(false);
  const [seoIntents, setSeoIntents] = useState<SeoIntents | null>(null);
  const [readyLocation, setReadyLocation] = useState<string | null>(null);
  const [routeAnnouncement, setRouteAnnouncement] = useState("");
  const languageRequestRef = useRef(0);
  const pendingRouteFocusRef = useRef<string | null>(location);
  const chapterMatch = pathname.match(/^\/chapter\/([^/]+)\/?$/);
  const chapter = chapterMatch ? atlas.chapterMap.get(chapterMatch[1]) : undefined;
  const partMatch = pathname.match(/^\/part\/([^/]+)\/?$/);
  const part = partMatch ? atlas.partMap.get(partMatch[1]) : undefined;
  const essayMatch = pathname.match(/^\/essay\/([^/]+)\/?$/);
  const transcriptAudio = matchEssayAudioRoute(pathname);
  const essay = essayMatch
    ? atlas.essayMap.get(essayMatch[1])
    : transcriptAudio
      ? atlas.essayMap.get(transcriptAudio.slug)
      : undefined;
  const socialImage = essay?.image
    ?? (chapter ? atlas.partMap.get(chapter.part)?.image : undefined)
    ?? part?.image
    ?? getHomeIllustration(language);
  const isKnownRoute = ["/", "/method", "/sources", "/library", "/authors"].includes(pathname)
    || Boolean(part || chapter || essay);

  useEffect(() => {
    let active = true;
    import("./seo-intents.json").then((module) => {
      if (active) setSeoIntents(module.default as SeoIntents);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#101816" : "#e8e2d6");
  }, [theme]);

  useEffect(() => {
    document.documentElement.dataset.readingSize = readingSize;
    localStorage.setItem(SIZE_KEY, readingSize);
  }, [readingSize]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dataset.language = language;
    localStorage.setItem(LANGUAGE_KEY, language);
  }, [language]);

  useEffect(() => {
    languageRequestRef.current += 1;
  }, [location]);

  useEffect(() => {
    const requestFocus = (event: Event) => {
      const requestedLocation = (event as CustomEvent<{ location?: unknown }>).detail?.location;
      if (typeof requestedLocation === "string") {
        pendingRouteFocusRef.current = requestedLocation;
        setRouteAnnouncement("");
      }
    };
    window.addEventListener(ROUTE_FOCUS_EVENT, requestFocus);
    return () => window.removeEventListener(ROUTE_FOCUS_EVENT, requestFocus);
  }, []);

  useEffect(() => {
    const openSearch = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
      if (event.key === "/" && !typing) {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, []);

  useEffect(() => {
    if (readyLocation !== location) return;
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    let timer: number | undefined;
    const storageKey = `${SCROLL_PREFIX}${publicPathname}`;
    const saveNow = () => {
      window.clearTimeout(timer);
      localStorage.setItem(storageKey, String(Math.round(window.scrollY)));
    };
    const save = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(saveNow, 120);
    };
    const restore = () => {
      const hash = window.location.hash.slice(1);
      const root = document.documentElement;
      const previousScrollBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto";
      try {
        if (hash) {
          let targetId = hash;
          try {
            targetId = decodeURIComponent(hash);
          } catch {
            // Keep the literal fragment when it contains malformed percent-encoding.
          }
          document.getElementById(targetId)?.scrollIntoView({ block: "start", behavior: "auto" });
        } else {
          const stored = Number(localStorage.getItem(storageKey) ?? 0);
          window.scrollTo({ top: Number.isFinite(stored) ? stored : 0, behavior: "auto" });
        }
      } finally {
        root.style.scrollBehavior = previousScrollBehavior;
      }
    };
    const frame = window.requestAnimationFrame(restore);
    window.addEventListener("scroll", save, { passive: true });
    window.addEventListener("beforeunload", saveNow);
    window.addEventListener("atlas2:before-navigate", saveNow);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.removeEventListener("scroll", save);
      window.removeEventListener("beforeunload", saveNow);
      window.removeEventListener("atlas2:before-navigate", saveNow);
    };
  }, [location, publicPathname, readyLocation]);

  useEffect(() => {
    if (readyLocation !== location || pendingRouteFocusRef.current !== location) return;
    const requestedLocation = location;

    const frame = window.requestAnimationFrame(() => {
      if (pendingRouteFocusRef.current !== requestedLocation) return;
      pendingRouteFocusRef.current = null;

      const hashIndex = requestedLocation.indexOf("#");
      const rawHash = hashIndex >= 0 ? requestedLocation.slice(hashIndex + 1) : "";
      let targetId = rawHash;
      if (rawHash) {
        try {
          targetId = decodeURIComponent(rawHash);
        } catch {
          // Keep the literal fragment when it contains malformed percent-encoding.
        }
      }

      const routeHeading = document.querySelector<HTMLElement>("#main-content h1");
      const target = targetId
        ? document.getElementById(targetId) ?? routeHeading
        : routeHeading;
      const announcementHeading = target?.matches("h1, h2, h3")
        ? target
        : target?.querySelector<HTMLElement>("h1, h2, h3") ?? routeHeading;
      setRouteAnnouncement(announcementHeading?.textContent?.trim() || document.title);
      if (!target) return;

      const hadTabIndex = target.hasAttribute("tabindex");
      if (!hadTabIndex) target.tabIndex = -1;
      target.focus({ preventScroll: true });

      if (!hadTabIndex) {
        target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location, readyLocation]);

  const page = useMemo(() => {
    if (pathname === "/") return <HomePage />;
    if (pathname === "/method") return <MethodPage />;
    if (pathname === "/sources") return <SourcesPage />;
    if (pathname === "/library") return <LibraryPage />;
    if (pathname === "/authors") return <AuthorsPage theme={theme} />;
    if (partMatch) return part ? <PartPage part={part} /> : <NotFoundPage />;
    if (transcriptAudio) return <TranscriptPage audio={transcriptAudio} key={transcriptAudio.audioId} />;
    if (essayMatch) return essay ? <EssayPage essay={essay} theme={theme} /> : <NotFoundPage />;
    if (chapterMatch) return chapter ? <ChapterPage chapter={chapter} theme={theme} /> : <NotFoundPage />;
    return <NotFoundPage />;
  }, [chapter, chapterMatch, essay, essayMatch, part, partMatch, pathname, theme, transcriptAudio]);

  useEffect(() => {
    if (!seoIntents) return;
    const fixedLabels = seoLabels[language];
    const intent = seoIntents[language] as SeoIntentCopy;
    const audioCopy = transcriptAudio?.copy[language];
    const audioSeo = transcriptAudio ? AUDIO_SEO_COPY[language] : undefined;
    const utilityKey = pathname === "/library" ? "library"
      : pathname === "/sources" ? "sources"
        : pathname === "/method" ? "method"
          : pathname === "/authors" ? "authors"
            : pathname === "/" ? "home"
              : undefined;
    const label = audioCopy && audioSeo
      ? `${audioCopy.transcriptTitle}: ${audioSeo.suffix}`
      : part ? intent.partTitles[part.id] ?? part.title
        : chapter ? intent.chapterTitles[chapter.slug] ?? chapter.navTitle
          : essay ? intent.essayTitles[essay.slug] ?? essay.title
            : utilityKey ? fixedLabels[utilityKey]
              : fixedLabels.missing;
    const pageTitle = `${label} — ${fixedLabels.brand}`;
    const rawDescription = audioSeo && essay
      ? audioSeo.description(essay.title)
      : part?.description ?? chapter?.lead ?? essay?.lead ?? (utilityKey ? intent.utilityDescriptions[utilityKey] : fixedLabels.description);
    const description = rawDescription.length > 180 ? `${rawDescription.slice(0, 177).trimEnd()}…` : rawDescription;
    const canonicalUrl = new URL(publicPathname, PUBLIC_ORIGIN).href;
    const socialImageUrl = new URL(socialImage.src, PUBLIC_ORIGIN).href;
    const openGraphLocale = language === "ru" ? "ru_RU" : language === "uk" ? "uk_UA" : "en_US";
    const alternateOpenGraphLocales = ["en_US", "uk_UA", "ru_RU"].filter((locale) => locale !== openGraphLocale);
    const setMeta = (selector: string, content: string) => document.querySelector<HTMLMetaElement>(selector)?.setAttribute("content", content);
    const setAlternate = (hreflang: string, href: string) => {
      let link = document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hreflang}"]`);
      if (!link) {
        link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = hreflang;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    document.title = pageTitle;
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', pageTitle);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[property="og:locale"]', openGraphLocale);
    setMeta("#open-graph-locale-alternate-1", alternateOpenGraphLocales[0]);
    setMeta("#open-graph-locale-alternate-2", alternateOpenGraphLocales[1]);
    setMeta('meta[property="og:type"]', chapter || essay ? "article" : "website");
    setMeta('meta[property="og:image"]', socialImageUrl);
    setMeta('meta[property="og:image:secure_url"]', socialImageUrl);
    setMeta('meta[property="og:image:type"]', "image/webp");
    setMeta('meta[property="og:image:width"]', String(socialImage.width ?? 1672));
    setMeta('meta[property="og:image:height"]', String(socialImage.height ?? 941));
    setMeta('meta[property="og:image:alt"]', socialImage.alt);
    setMeta('meta[name="twitter:title"]', pageTitle);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:image"]', socialImageUrl);
    setMeta('meta[name="twitter:image:alt"]', socialImage.alt);
    setMeta('meta[name="robots"]', isKnownRoute
      ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      : "noindex, follow");

    const canonical = document.querySelector<HTMLLinkElement>('#canonical-url');
    const alternates = ["en", "uk", "ru", "x-default"];
    if (isKnownRoute) {
      canonical?.setAttribute("href", canonicalUrl);
      const englishUrl = new URL(localizeHref(pathname, "en"), PUBLIC_ORIGIN).href;
      setAlternate("en", englishUrl);
      setAlternate("uk", new URL(localizeHref(pathname, "uk"), PUBLIC_ORIGIN).href);
      setAlternate("ru", new URL(localizeHref(pathname, "ru"), PUBLIC_ORIGIN).href);
      setAlternate("x-default", englishUrl);
    } else {
      canonical?.removeAttribute("href");
      for (const hreflang of alternates) {
        document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hreflang}"]`)?.removeAttribute("href");
      }
    }
  }, [chapter, essay, isKnownRoute, language, part, pathname, publicPathname, seoIntents, socialImage, transcriptAudio]);

  useEffect(() => {
    if (!isKnownRoute) return;
    const locationSuffix = location.slice(requestedPathname.length);
    trackPageView(new URL(`${publicPathname}${locationSuffix}`, PUBLIC_ORIGIN).href, document.title);
  }, [isKnownRoute, location, publicPathname, requestedPathname]);

  useEffect(() => {
    if (readyLocation !== location) return;
    const quoteId = getXQuoteIdFromHash(window.location.hash);
    if (!quoteId || !isKnownRoute) return;
    const target = document.getElementById(quoteId);
    if (!target) return;

    const eventKey = `${language}:${quoteId}`;
    const parameters = { quote_id: quoteId, language };
    trackAtlasEventOnce("quote_landing", eventKey, parameters);
    if (!("IntersectionObserver" in window)) return;

    let observer: IntersectionObserver | undefined;
    let resizeFrame = 0;
    let visibilityReached = false;
    const observe = () => {
      observer?.disconnect();
      if (visibilityReached) return;
      const nextObserver = new IntersectionObserver((entries) => {
        const quoteEntry = entries.find((entry) => entry.target === target);
        if (!quoteEntry?.isIntersecting || quoteEntry.intersectionRatio < 0.5) return;
        visibilityReached = true;
        nextObserver.disconnect();
        trackAtlasEventOnce("quote_visible", eventKey, parameters);
      }, {
        root: null,
        rootMargin: `-${fixedTopUiInset()}px 0px 0px 0px`,
        threshold: 0.5,
      });
      observer = nextObserver;
      nextObserver.observe(target);
    };
    const updateObserver = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(observe);
    };

    observe();
    window.addEventListener("resize", updateObserver);
    return () => {
      window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener("resize", updateObserver);
      observer?.disconnect();
    };
  }, [isKnownRoute, language, location, readyLocation]);

  const setLanguage = (nextLanguage: Language) => {
    const suffix = location.slice(requestedPathname.length);
    const href = localizeHref(`${pathname}${suffix}`, nextLanguage);
    const requestId = ++languageRequestRef.current;
    void preloadAtlasLocale(nextLanguage).then(
      () => {
        if (languageRequestRef.current === requestId) navigate(href, { replace: true });
      },
      () => {
        if (languageRequestRef.current === requestId) window.location.replace(href);
      },
    );
  };

  return (
    <LocaleProvider locale={atlas}>
      <Shell
        pathname={pathname}
        theme={theme}
        readingSize={readingSize}
        language={language}
        onSetLanguage={setLanguage}
        onToggleTheme={() => setTheme((value) => value === "light" ? "dark" : "light")}
        onToggleReadingSize={() => setReadingSize((value) => value === "normal" ? "large" : "normal")}
        onOpenSearch={() => setSearchOpen(true)}
      >
        <p className="sr-only route-announcer" role="status" aria-live="polite" aria-atomic="true">
          {routeAnnouncement}
        </p>
        <Suspense
          fallback={(
            <div className="route-loading" role="status" aria-live="polite" aria-atomic="true" aria-busy="true">
              <span className="route-loading-label">{ROUTE_LOADING_COPY[language]}</span>
            </div>
          )}
        >
          <RouteReady location={location} onReady={setReadyLocation}>
            {page}
          </RouteReady>
        </Suspense>
        <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
        <AnalyticsConsent />
      </Shell>
    </LocaleProvider>
  );
}
